/**
 * Variant Gallery Swap (Prestige/Impact-based DeLusso theme)
 *
 * Mechanism: All variant gallery images (custom.termekkepek metafield) and the
 * native product media are pre-rendered in the gallery. Non-active items are
 * marked with the `hidden` attribute; on variant change we only toggle
 * visibility — no DOM injection, no carousel re-init.
 *
 * v2: works with dynamically injected galleries too (quick-buy modal).
 * The script is loaded globally from theme.liquid; a MutationObserver arms
 * every <product-gallery> that appears later (modal fetch + product-rerender).
 */
(function () {
  'use strict';

  const VARIANT_INPUT_SELECTOR = 'input[name="id"]';
  const GALLERY_SELECTOR = 'product-gallery';

  function applyVariantToGallery(gallery, variantId) {
    const variantsWithCustom = (gallery.dataset.variantsWithCustomGallery || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const hasCustomGallery = variantsWithCustom.includes(String(variantId));

    // Native product media (and their thumbnails/dots) — visible only when
    // the current variant does NOT have a custom gallery.
    const nativeItems = gallery.querySelectorAll('[data-native-media="true"]');
    nativeItems.forEach((el) => {
      if (hasCustomGallery) {
        if (!el.hasAttribute('hidden')) {
          el.setAttribute('hidden', '');
          el.setAttribute('data-vg-hidden', 'true');
        }
      } else {
        if (el.getAttribute('data-vg-hidden') === 'true') {
          el.removeAttribute('hidden');
          el.removeAttribute('data-vg-hidden');
        }
        // Server-side hidden natives (hide_native_initial) must also be
        // restored when switching to a variant without custom gallery.
        if (gallery.dataset.hideNativeInitial === 'true' && el.hasAttribute('hidden')) {
          const filtered = gallery.getAttribute('filtered-indexes') || '[]';
          const pos = el.dataset.mediaPosition;
          let isFiltered = false;
          try {
            isFiltered = pos ? JSON.parse(filtered).includes(parseInt(pos, 10)) : false;
          } catch (e) { /* noop */ }
          if (!isFiltered) el.removeAttribute('hidden');
        }
      }
    });

    // Variant-specific images: visible only when their variant ID matches.
    const variantImages = gallery.querySelectorAll('[data-variant-image="true"],[data-variant-thumbnail="true"],[data-variant-dot="true"]');
    variantImages.forEach((el) => {
      if (el.dataset.variantId === String(variantId)) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });

    gallery.dataset.currentVariantId = String(variantId);

    const carousel = gallery.querySelector('scroll-carousel');
    if (carousel) {
      const firstVisible = carousel.querySelector('.product-gallery__media:not([hidden])');
      if (firstVisible && typeof firstVisible.scrollIntoView === 'function') {
        try {
          firstVisible.scrollIntoView({ behavior: 'instant', inline: 'start', block: 'nearest' });
        } catch (e) {
          firstVisible.scrollIntoView({ inline: 'start', block: 'nearest' });
        }
      }
      carousel.dispatchEvent(new CustomEvent('carousel:updated', { bubbles: true }));
    }

    const thumbList = gallery.querySelector('product-gallery-navigation');
    if (thumbList) {
      const thumbs = thumbList.querySelectorAll('.product-gallery__thumbnail');
      let first = true;
      thumbs.forEach((thumb) => {
        if (!thumb.hasAttribute('hidden') && first) {
          thumb.setAttribute('aria-current', 'true');
          first = false;
        } else {
          thumb.setAttribute('aria-current', 'false');
        }
      });
    }
  }

  function applyVariant(variantId) {
    if (!variantId) return;
    document.querySelectorAll(GALLERY_SELECTOR).forEach((g) => applyVariantToGallery(g, variantId));
  }

  function getCurrentVariantId(scope) {
    const root = scope || document;
    const input = root.querySelector(VARIANT_INPUT_SELECTOR);
    return input ? input.value : null;
  }

  /**
   * Arm a single gallery: sync its visibility with its own current variant.
   * The variant is resolved from the gallery's linked form (form attribute),
   * falling back to the server-rendered data-current-variant-id.
   */
  function armGallery(gallery) {
    if (gallery.dataset.vgArmed === 'true') return;
    gallery.dataset.vgArmed = 'true';

    let variantId = null;
    const formId = gallery.getAttribute('form');
    if (formId) {
      const form = document.getElementById(formId);
      if (form) variantId = getCurrentVariantId(form);
    }
    if (!variantId) variantId = gallery.dataset.currentVariantId;
    if (variantId) applyVariantToGallery(gallery, variantId);
  }

  function armAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(GALLERY_SELECTOR).forEach(armGallery);
    if (root && root.matches && root.matches(GALLERY_SELECTOR)) armGallery(root);
  }

  function init() {
    armAll(document);

    // Theme dispatches 'variant:change' (bubbling) on variant selection —
    // delegated listener also covers galleries injected later (quick-buy).
    document.addEventListener('variant:change', (e) => {
      const variant = e.detail && e.detail.variant;
      const id = variant ? variant.id : getCurrentVariantId();
      if (id) applyVariant(id);
    });

    // Fallback: hidden variant input changes on static galleries.
    document.querySelectorAll(VARIANT_INPUT_SELECTOR).forEach((input) => {
      input.addEventListener('change', () => applyVariant(input.value));
      const observer = new MutationObserver(() => applyVariant(input.value));
      observer.observe(input, { attributes: true, attributeFilter: ['value'] });
    });

    // Arm dynamically injected galleries (quick-buy modal fetch,
    // product-rerender swaps). Injected <script> tags never execute,
    // so this observer is the only reliable hook.
    const domObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(GALLERY_SELECTOR)) {
            armGallery(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll(GALLERY_SELECTOR).forEach(armGallery);
          }
        }
      }
    });
    domObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
